import React, { useEffect, useState, useMemo } from 'react';
import { db, collection, onSnapshot } from '../../firebase';
import { Calendar, Clock, ChevronDown, ChevronUp, User, Search, AlertCircle, X, Users } from 'lucide-react';
import ReservationDetailsModal from './ReservationDetailsModal';
import styles from '../CSS/SpaceReservations.module.css';

const MONTH_MAP = {
  Enero: 1, Febrero: 2, Marzo: 3, Abril: 4, Mayo: 5, Junio: 6,
  Julio: 7, Agosto: 8, Septiembre: 9, Octubre: 10, Noviembre: 11, Diciembre: 12
};

const SpaceReservations = ({ space, onClose }) => {
  const [reservations, setReservations] = useState([]);
  const [periodicReservations, setPeriodicReservations] = useState([]);
  const [expandedMonths, setExpandedMonths] = useState({});
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const truncateText = (text, maxLength) => {
    if (!text || text.length <= maxLength) return text || '';
    return text.substring(0, maxLength) + '...';
  };

  const capitalize = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getDayNames = (daysArray) => {
    const dayNames = { 1: 'L', 2: 'M', 3: 'X', 4: 'J', 5: 'V' };
    return daysArray.map(d => dayNames[d]).join(', ');
  };

  useEffect(() => {
    const reservationsRef = collection(db, 'reservations');
    const unsubscribeReservations = onSnapshot(
      reservationsRef,
      (snapshot) => {
        const allRes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const filtered = allRes.filter(res => res.space === space.name);
        setReservations(filtered);
        setLoading(false);
      },
      (error) => {
        setError('Error al obtener las reservas');
        setLoading(false);
        console.error('Error al obtener reservas:', error);
      }
    );

    const periodicRef = collection(db, 'periodicReservations');
    const unsubscribePeriodic = onSnapshot(
      periodicRef,
      (snapshot) => {
        const allPeriodic = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const filtered = allPeriodic.filter(res => res.space === space.name);
        setPeriodicReservations(filtered);
      },
      (error) => console.error('Error al obtener periódicas:', error)
    );

    return () => {
      unsubscribeReservations();
      unsubscribePeriodic();
    };
  }, [space]);

  const unifiedReservations = useMemo(() => {
    const periodicGroups = {};
    const manualReservations = [];

    reservations.forEach(res => {
      if (res.type === 'periodic' && res.periodicId) {
        if (!periodicGroups[res.periodicId]) periodicGroups[res.periodicId] = [];
        periodicGroups[res.periodicId].push(res);
      } else {
        manualReservations.push(res);
      }
    });

    const unified = [];

    Object.keys(periodicGroups).forEach(periodicId => {
      const periodicMaster = periodicReservations.find(p => p.id === periodicId);
      if (periodicMaster) {
        unified.push({
          id: periodicId,
          type: 'periodic',
          isPeriodic: true,
          ...periodicMaster,
          instances: periodicGroups[periodicId]
        });
      }
    });

    manualReservations.forEach(res => {
      unified.push({ ...res, isPeriodic: false });
    });

    return unified;
  }, [reservations, periodicReservations]);

  const groupedReservations = useMemo(() => {
    const grouped = {};
    
    unifiedReservations
      .filter(res => {
        const matchesSearch = 
          res.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          res.reservedBy?.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          res.delegatedTo?.displayName?.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
      })
      .forEach(res => {
        const dateStr = res.isPeriodic ? res.startDate : res.date;
        const [year] = (dateStr || '').split('-');
        const dateObj = new Date(dateStr);
        const monthName = dateObj.toLocaleString('es-ES', { month: 'long' });
        const dateKey = `${capitalize(monthName)} ${year}`;
        
        if (!grouped[dateKey]) grouped[dateKey] = [];
        grouped[dateKey].push(res);
      });

    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => {
        const dateA = a.isPeriodic ? new Date(`${a.startDate}T${a.startTime}`) : new Date(`${a.date}T${a.startTime}`);
        const dateB = b.isPeriodic ? new Date(`${b.startDate}T${b.startTime}`) : new Date(`${b.date}T${b.startTime}`);
        return dateA - dateB;
      });
    });

    return grouped;
  }, [unifiedReservations, searchTerm]);

  const sortedMonthKeys = useMemo(() => {
    return Object.keys(groupedReservations).sort((a, b) => {
      const [monthA, yearA] = a.split(' ');
      const [monthB, yearB] = b.split(' ');
      if (yearA !== yearB) return parseInt(yearA) - parseInt(yearB);
      return MONTH_MAP[monthA] - MONTH_MAP[monthB];
    });
  }, [groupedReservations]);

  const toggleMonth = (monthKey) => {
    setExpandedMonths(prev => ({
      ...prev,
      [monthKey]: !prev[monthKey]
    }));
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Cargando reservas...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <AlertCircle className={styles.errorIcon} />
        <p>{error}</p>
        <button onClick={onClose} className={styles.btnSecondary}>
          Volver
        </button>
      </div>
    );
  }

  return (
    <div className={styles.reservationsContainer}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <h2 className={styles.title}>Reservas de {space.name}</h2>
          <button onClick={onClose} className={styles.closeButton} aria-label="Cerrar">
            <X />
          </button>
        </div>
        
        <div className={styles.searchContainer}>
          <Search className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Buscar por título o usuario..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </header>

      {sortedMonthKeys.length === 0 ? (
        <div className={styles.emptyState}>
          <Calendar className={styles.emptyIcon} />
          <p>No hay reservas registradas para este espacio.</p>
        </div>
      ) : (
        <div className={styles.monthsContainer}>
          {sortedMonthKeys.map((monthKey) => (
            <div key={monthKey} className={styles.monthBlock}>
              <div 
                className={styles.monthHeader}
                onClick={() => toggleMonth(monthKey)}
              >
                <h3>{monthKey}</h3>
                {expandedMonths[monthKey] ? 
                  <ChevronUp className={styles.toggleIcon} /> : 
                  <ChevronDown className={styles.toggleIcon} />
                }
              </div>
              
              {expandedMonths[monthKey] && (
                <div className={styles.reservationsList}>
                  {groupedReservations[monthKey].map((res) => (
                    <div 
                      key={res.id} 
                      className={styles.reservationCard}
                      onClick={() => setSelectedReservation(res)}
                    >
                      <div className={styles.cardHeader}>
                        <h4 className={styles.reservationTitle} title={res.title}>
                          {truncateText(res.title, 50)}
                        </h4>
                        {res.isPeriodic && (
                          <span className={styles.periodicBadge}>
                            Periódica
                          </span>
                        )}
                      </div>

                      <div className={styles.cardBody}>
                        <div className={styles.dateTimeRow}>
                          <div className={styles.infoItem}>
                            <Calendar className={styles.icon} />
                            {res.isPeriodic ? (
                              <span>{formatDate(res.startDate)} - {formatDate(res.endDate)}</span>
                            ) : (
                              <span>{formatDate(res.date)}</span>
                            )}
                          </div>
                          <div className={styles.infoItem}>
                            <Clock className={styles.icon} />
                            <span>{res.startTime} - {res.endTime}</span>
                          </div>
                        </div>

                        {res.isPeriodic && (
                          <div className={styles.periodicInfo}>
                            <span className={styles.daysLabel}>
                              {getDayNames(res.daysOfWeek)}
                            </span>
                            <span className={styles.sessionsLabel}>
                              {res.occurrencesCount} sesiones
                            </span>
                          </div>
                        )}

                        <div className={styles.usersRow}>
                          <div className={styles.userInfo}>
                            <User className={styles.userIcon} />
                            <div className={styles.userDetails}>
                              <span className={styles.userLabel}>Reservado por:</span>
                              <span className={styles.userName} title={res.reservedBy?.displayName}>
                                {truncateText(res.reservedBy?.displayName || 'Usuario desconocido', 25)}
                              </span>
                            </div>
                          </div>

                          {res.delegatedTo && (
                            <div className={styles.userInfo}>
                              <Users className={styles.userIcon} />
                              <div className={styles.userDetails}>
                                <span className={styles.userLabel}>Asignado a:</span>
                                <span className={styles.userName} title={res.delegatedTo?.displayName}>
                                  {truncateText(res.delegatedTo?.displayName, 25)}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {selectedReservation && (
        <ReservationDetailsModal
          reservation={selectedReservation}
          onClose={() => setSelectedReservation(null)}
        />
      )}
    </div>
  );
};

export default SpaceReservations;